import React, { useState, useContext, useCallback, useEffect, forwardRef } from 'react';
import { useCookies } from 'react-cookie';

import { GlobalComponentsContext } from 'context/GlobalComponentsContext';
import { FiltersContext, initialState } from 'context/GlobalFiltersContext';

import { Button } from 'components/Button';
import { FilterModal } from 'components/FilterModal';
import { FilterBadge } from 'components/FilterBadge';

import { getDependentFilters } from 'components/HeaderFilters/getDependentFilters';

import { AuthService, HeaderFilterService } from 'services/main';

import { companies, unitGroups, unitSubgroups, units, states, cities, providers, filtersToUnmark } from './constants';

import { StyledFiltersContainer } from './styled';
import { localStorageKeys } from 'constants/localStorage';
import { getLocalAuthStorage } from 'utils/localStorage';
import { URLS } from 'constants/urls';
import { useHistory } from 'react-router-dom';
import { PermissionContext } from 'context';

const HeaderFilters = forwardRef((props, ref) => {
  const { defaultOpen = null } = props;
  const { globalComponentsContext, setGlobalComponentsContext } = useContext(GlobalComponentsContext);
  const { headerFiltersContext, setFiltersContext } = useContext(FiltersContext);
  const { setPermissionContext } = useContext(PermissionContext);

  // TODO HOIST THIS INTO CONSTANT
  const [cookies, setCookie, removeCookie] = useCookies([
    'companies',
    'units',
    'unitGroups',
    'unitSubgroups',
    'states',
    'cities',
    'providers',
  ]);

  let { isFiltersOpen } = globalComponentsContext;
  const { headerFilters } = headerFiltersContext;

  const [isFiltersVisible, setIsFilterVisible] = useState(defaultOpen || isFiltersOpen);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [modalOptions, setModalOptions] = useState({
    open: false,
    propertyKey: null,
  });

  const { user } = getLocalAuthStorage();

  const isCommonUser = user?.permissionType === `user`;

  const closeModal = () => {
    setModalOptions({
      open: false,
    });
  };

  const onSelectFilter = async (itemSelected, filterKey) => {
    // NOVO objeto (não muta o estado original)
    let newFilters = { ...headerFilters };

    // Limpa os dependentes SEM gerar estado intermediário
    filtersToUnmark[filterKey].forEach(filter => {
      newFilters[filter] = false;
    });

    // Aplica os dependentes retornados do backend
    const dependentsFilter = await getDependentFilters({
      filterType: filterKey,
      filterResponse: itemSelected,
    });

    if (dependentsFilter?.length > 0) {
      dependentsFilter.forEach(df => {
        newFilters[df.filter] = df.value;
      });
    }

    // Atualiza cookies
    removeCookie(filterKey, { path: '/', domain: URLS.cookieDomain });
    setCookie(
      filterKey,
      {
        id: String(itemSelected.id),
        name: String(itemSelected.name),
      },
      { path: '/', domain: URLS.cookieDomain },
    );

    // Define o valor selecionado
    newFilters[filterKey] = { ...itemSelected };

    // Atualiza o contexto com APENAS UMA MUDANÇA
    setFiltersContext({
      ...headerFiltersContext,
      headerFilters: newFilters,
    });

    closeModal();
  };

  const removeFilters = useCallback(
    filtersToUnmark => {
      const activeFilters = { ...headerFilters };
      filtersToUnmark.forEach(filter => {
        removeCookie(filter, { path: '/', domain: URLS.cookieDomain });
        activeFilters[filter] = false;
      });

      setFiltersContext({
        ...headerFiltersContext,
        headerFilters: activeFilters,
      });
      //window.location.reload();
    },
    [headerFilters, headerFiltersContext, removeCookie, setFiltersContext],
  );

  const getModalOptionsData = async () => {
    try {
      const request = HeaderFilterService[currentFilter];
      if (typeof request !== 'function') {
        console.error(`Handler for filter "${currentFilter}" not found.`);
        return;
      }
      const { data } = await request({
        empresaId: cookies?.companies?.id,
        grupoUnidadeId: cookies?.unitGroups?.id,
        subGrupoUnidadeId: cookies?.unitSubgroups?.id,
        unidadeId: cookies?.units?.id,
        estadoId: cookies?.states?.id,
        cidadeId: cookies?.cities?.id,
        // equipeId: cookies?.teams?.id,
        fornecedorId: cookies?.providers?.id,
      });
      return data;
    } catch (err) {
      return;
    }
  };
  const history = useHistory();
  const getCompanyName = async id => {
    return new Promise(async (resolve, reject) => {
      HeaderFilterService.company(id)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  };
  const loadPermissions = useCallback(
    async ({ empresaId, unidadeId }) => {
      const token = JSON.parse(localStorage.getItem(localStorageKeys.authContext))?.token;
      if (!empresaId) return; // empresa é obrigatória

      const session = await AuthService.refreshSession({
        empresaId,
        unidadeId: unidadeId ?? null,
        token,
      });

      const permissoes = session?.userSession?.permissoesList ?? [];
      const canExibirDocs = permissoes.includes(956);
      const canSalvarDocs = permissoes.includes(684);

      setPermissionContext(prev => ({
        ...prev,
        documentos: {
          exibir: canExibirDocs,
          salvar: canSalvarDocs,
        },
      }));
    },
    [setPermissionContext],
  );

  // TODO REFACTOR FOLLOWING CODE
  const allFilters = ['companies', 'unitGroups', 'unitSubgroups', 'units', 'states', 'cities', 'teams', 'providers'];
  const updateHeaderFiltersByCookies = useCallback(async () => {
    const cookieCompanyId = cookies?.companies?.id ? parseInt(cookies.companies.id) : undefined;
    const cookieUnitId = cookies?.units?.id ? parseInt(cookies.units.id) : undefined;
    const updatedHeaderFilters = {};

    try {
      // Empresa
      if (!cookieCompanyId) {
        updatedHeaderFilters.companies = false;
      } else {
        const company = await getCompanyName(cookieCompanyId);
        updatedHeaderFilters.companies = {
          id: cookieCompanyId,
          name: company.nomeFantasia,
          logoPath: company.logomarcaPath,
          tipoNome: company.tipoNome,
        };
        setCookie(
          'companies',
          { id: cookieCompanyId, name: company.nomeFantasia, tipoNome: company.tipoNome },
          { path: '/', domain: URLS.cookieDomain },
        );
      }

      // Unidade
      if (!cookieUnitId) {
        updatedHeaderFilters.units = false;
      } else {
        const unitRes = await HeaderFilterService.unit(cookieCompanyId, cookieUnitId);
        updatedHeaderFilters.units = {
          id: cookieUnitId,
          name: unitRes.data.nome,
          logoPath: unitRes.data.logomarcaPath,
        };
        setCookie('units', { id: cookieUnitId, name: unitRes.data.nome }, { path: '/', domain: URLS.cookieDomain });
      }

      // Outros filtros (mantém o que tiver, senão false)
      for (const key of allFilters) {
        if (['companies', 'units'].includes(key)) continue;

        const cookieValue = cookies?.[key];
        const isValid = cookieValue && cookieValue.id && String(cookieValue.id).trim() !== '';

        updatedHeaderFilters[key] = isValid ? cookieValue : false;
      }
      setFiltersContext(headerFiltersContext => ({
        ...headerFiltersContext,
        headerFilters: updatedHeaderFilters,
      }));
    } catch (err) {
      console.error('Erro ao carregar filtros:', err);
      history.push('/logout');
      setFiltersContext(initialState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // use saved header filters effect

  useEffect(() => {
    updateHeaderFiltersByCookies();
  }, [updateHeaderFiltersByCookies]);
  // Quando headerFilters atualiza por cookies → carregar permissões
  useEffect(() => {
    const empresaId = headerFilters?.companies?.id;
    const unidadeId = headerFilters?.units?.id ?? null;

    if (empresaId) {
      loadPermissions({ empresaId, unidadeId });
    }
  }, [headerFilters?.companies?.id, headerFilters?.units?.id, loadPermissions]);

  useEffect(() => {
    setIsFilterVisible(globalComponentsContext.isFiltersOpen);
  }, [globalComponentsContext]);
  return (
    <div ref={ref}>
      <StyledFiltersContainer isOpen={isFiltersVisible}>
        <Button
          theme="white"
          size="small"
          data-cy="toggle-filters-button"
          onClick={() =>
            setGlobalComponentsContext({
              ...globalComponentsContext,
              isFiltersOpen: !isFiltersVisible,
            })
          }
        >
          {isFiltersVisible ? '+' : '-'} Filtros
        </Button>

        <FilterBadge
          label="Empresa"
          currentValue={cookies?.companies?.name}
          data={cookies?.companies?.id}
          disableOpen={isCommonUser}
          disableDelete={true}
          id="select-company"
          onClick={() => {
            setCurrentFilter(() => companies);
            setModalOptions(() => ({
              title: 'Selecionar - empresas',
              open: true,
              propertyKey: 'name',
            }));
          }}
          isOpen={isFiltersVisible}
        />

        <FilterBadge
          label="Grupos de Unidades de Manutenção"
          currentValue={cookies?.unitGroups?.name}
          data={cookies?.unitGroups?.id}
          onClick={() => {
            setCurrentFilter(() => unitGroups);
            setModalOptions(() => ({
              title: 'Selecionar - grupos',
              open: true,
              propertyKey: 'name',
            }));
          }}
          onClickX={() => removeFilters(filtersToUnmark.unitGroups)}
          isOpen={isFiltersVisible}
        />

        <FilterBadge
          label="Subgrupos de Unidades"
          currentValue={cookies?.unitSubgroups?.name}
          data={cookies?.unitSubgroups?.id}
          onClick={() => {
            setCurrentFilter(() => unitSubgroups);
            setModalOptions(() => ({
              title: 'Selecionar - subgrupos',
              open: true,
              propertyKey: 'name',
            }));
          }}
          onClickX={() => removeFilters(filtersToUnmark.unitSubgroups)}
          isOpen={isFiltersVisible}
        />

        <FilterBadge
          label="Unidades de Manutenção"
          currentValue={cookies?.units?.name}
          id="select-maintenance-unit"
          data={cookies?.units?.id}
          onClick={() => {
            setCurrentFilter(() => units);
            setModalOptions(() => ({
              title: 'Selecionar - unidades',
              open: true,
              propertyKey: 'name',
            }));
          }}
          onClickX={() => removeFilters(filtersToUnmark.units)}
          isOpen={isFiltersVisible}
        />

        <FilterBadge
          label="Estado"
          currentValue={cookies?.states?.name}
          data={cookies?.states?.id}
          onClick={() => {
            setCurrentFilter(() => states);
            setModalOptions(() => ({
              title: 'Selecionar - estados',
              open: true,
              propertyKey: 'name',
            }));
          }}
          onClickX={() => removeFilters(filtersToUnmark.states)}
          isOpen={isFiltersVisible}
        />

        <FilterBadge
          label="Cidade"
          currentValue={cookies?.cities?.name}
          data={cookies?.cities?.id}
          onClick={() => {
            setCurrentFilter(() => cities);
            setModalOptions(() => ({
              title: 'Selecionar - cidades',
              open: true,
              propertyKey: 'name',
            }));
          }}
          onClickX={() => removeFilters(filtersToUnmark.cities)}
          isOpen={isFiltersVisible}
        />

        <FilterBadge
          label="Fornecedores"
          currentValue={cookies?.providers?.name}
          data={cookies?.providers?.id}
          onClick={() => {
            setCurrentFilter(() => providers);
            setModalOptions(() => ({
              title: 'Selecionar - fornecedores',
              open: true,
              propertyKey: 'name',
            }));
          }}
          onClickX={() => removeFilters(filtersToUnmark.providers)}
          isOpen={isFiltersVisible}
        />
      </StyledFiltersContainer>
      {modalOptions.open && (
        <FilterModal
          id="headers-filters-modal"
          options={modalOptions}
          closeModal={closeModal}
          onSelect={item => onSelectFilter(item, currentFilter)}
          getData={getModalOptionsData}
        />
      )}
    </div>
  );
});

export { HeaderFilters };
